import { isToolUIPart } from "ai";
import {
  askUserQuestionInputSchema,
  askUserQuestionResponseSchema,
  type AskUserQuestion,
  type AskUserQuestionAnswer,
  type AskUserQuestionInput,
  type AskUserQuestionResponse,
  type ChatMessage,
} from "@tracify/shared";

type ToolPart = Extract<
  ChatMessage["parts"][number],
  { type: `tool-${string}` }
>;

export type PendingAskUserQuestion = AskUserQuestionInput & {
  toolCallId: string;
};

export type AskUserQuestionTranscriptModel =
  | {
      status: "pending";
      questions: AskUserQuestion[];
    }
  | {
      status: "answered";
      questions: AskUserQuestion[];
      answers: AskUserQuestionAnswer[];
    }
  | {
      status: "declined";
      questions: AskUserQuestion[];
    };

export function isAskUserQuestionToolPart(
  part: ChatMessage["parts"][number] | undefined
): part is ToolPart {
  return Boolean(
    part && isToolUIPart(part) && part.type === "tool-askUserQuestion"
  );
}

export function parseAskUserQuestionInput(
  input: unknown
): AskUserQuestionInput | null {
  const parsed = askUserQuestionInputSchema.safeParse(input);
  return parsed.success ? parsed.data : null;
}

export function parseAskUserQuestionResponse(
  output: unknown
): AskUserQuestionResponse | null {
  const parsed = askUserQuestionResponseSchema.safeParse(output);
  return parsed.success ? parsed.data : null;
}

function getResolvedAskUserQuestionToolCallIds(
  parts: ChatMessage["parts"] | undefined
) {
  const resolvedToolCallIds = new Set<string>();

  for (const part of parts ?? []) {
    if (!isAskUserQuestionToolPart(part) || part.state !== "output-available") {
      continue;
    }

    const response = parseAskUserQuestionResponse(part.output);
    if (!response) {
      continue;
    }

    resolvedToolCallIds.add(part.toolCallId);
  }

  return resolvedToolCallIds;
}

export function getLatestPendingAskUserQuestion(
  messages: ChatMessage[]
): PendingAskUserQuestion | null {
  const latestAssistantMessage = [...messages]
    .reverse()
    .find((message) => message.role === "assistant");

  if (!latestAssistantMessage) {
    return null;
  }

  const resolvedToolCallIds = getResolvedAskUserQuestionToolCallIds(
    latestAssistantMessage.parts
  );

  for (const part of [...(latestAssistantMessage.parts ?? [])].reverse()) {
    if (!isAskUserQuestionToolPart(part) || part.state !== "input-available") {
      continue;
    }

    if (resolvedToolCallIds.has(part.toolCallId)) {
      continue;
    }

    const parsedInput = parseAskUserQuestionInput(part.input);
    if (!parsedInput) {
      continue;
    }

    return {
      toolCallId: part.toolCallId,
      ...parsedInput,
    };
  }

  return null;
}

export function getAskUserQuestionTranscriptModel(
  part: ChatMessage["parts"][number] | undefined
): AskUserQuestionTranscriptModel | null {
  if (!isAskUserQuestionToolPart(part)) {
    return null;
  }

  const input = parseAskUserQuestionInput(part.input);
  if (!input) {
    return null;
  }

  if (part.state === "input-available") {
    return {
      status: "pending",
      questions: input.questions,
    };
  }

  if (part.state === "output-available") {
    const response = parseAskUserQuestionResponse(part.output);
    if (!response) {
      return null;
    }

    if (!response.approved) {
      return {
        status: "declined",
        questions: input.questions,
      };
    }

    return {
      status: "answered",
      questions: input.questions,
      answers: response.answers,
    };
  }

  return null;
}
